import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdvancedTable from '../components/AdvancedTable';
import FeaturedStockCard from '../components/FeaturedStockCard';
import UpgradeModal from '../components/UpgradeModal';
import MarketStatusBadge from '../components/MarketStatusBadge';
import MobileHeader from '../components/MobileHeader';
import MobileAnomalyCard from '../components/MobileAnomalyCard';
import MarketPulse from '../components/MarketPulse';
import ProgressTracker from '../components/ProgressTracker';
import { useAuth } from '../contexts/AuthContext';
import { useTour, TOUR_IDS } from '../contexts/TourContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, Activity, RefreshCw, HelpCircle, Lightbulb, Lock, Clock, ChevronDown, LogIn, Radio, Filter } from 'lucide-react';

// Hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const REFRESH_INTERVAL = 60000;

// Time filter options
const TIME_FILTERS = [
  { label: '1 Hour', value: '1h', backendParam: 'HOURS:1', proOnly: true },
  { label: '4 Hours', value: '4h', backendParam: 'HOURS:4', proOnly: true },
  { label: '12 Hours', value: '12h', backendParam: 'HOURS:12', proOnly: true },
  { label: '24 Hours', value: '24h', backendParam: 'HOURS:24', proOnly: true },
  { label: '3 Days', value: '3d', backendParam: 'HOURS:72', proOnly: false },
  { label: '7 Days', value: '7d', backendParam: '7D_TRADING', proOnly: false },
];

// Restricted intraday timeframes for free users
const RESTRICTED_INTRADAY = ['1h', '4h', '12h', '24h'];

// Helper to get backend param from filter value
const getBackendTimeRange = (filterValue) => {
  const filter = TIME_FILTERS.find(f => f.value === filterValue);
  return filter ? filter.backendParam : '7D_TRADING';
};

// System activity messages for rotation
const SYSTEM_ACTIVITY_MESSAGES = [
  'Scanning S&P 500 stocks for abnormal price deviations',
  'Evaluating unusual stock volume behavior',
  'Tracking institutional stock positioning changes',
  'Reviewing political stock trading disclosures'
];

// System Status Indicator Component - Uses server-synced timestamps
const SystemStatusIndicator = ({ scanSchedule, isMarketOpen, onScanDue }) => {
  const [countdown, setCountdown] = useState('--:--');
  const [lastEvalTime, setLastEvalTime] = useState('--:--');
  const [isUpdating, setIsUpdating] = useState(false);
  const hasTriggeredRefresh = useRef(false);
  
  useEffect(() => {
    // Update last evaluation time from server timestamp
    if (scanSchedule?.last_scan_at) {
      const lastScan = new Date(scanSchedule.last_scan_at);
      setLastEvalTime(lastScan.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      }));
      // Reset refresh trigger when we get new scan data
      hasTriggeredRefresh.current = false;
    }
    
    // Calculate countdown based on server-provided next_scan_at
    const calculateCountdown = () => {
      if (!scanSchedule?.next_scan_at) {
        setCountdown('--:--');
        setIsUpdating(false);
        return;
      }
      
      const nextScan = new Date(scanSchedule.next_scan_at);
      const now = new Date();
      const diff = nextScan - now;
      
      // If next_scan_at is in the past, show "Updating..." and trigger refresh
      if (diff <= 0) {
        setCountdown('00:00');
        setIsUpdating(true);
        
        // Trigger refresh once when countdown reaches zero
        // Wait 5 seconds for scan to complete, then refresh
        if (!hasTriggeredRefresh.current && onScanDue) {
          hasTriggeredRefresh.current = true;
          console.log('[Dashboard] Scan due - auto-refreshing in 5 seconds...');
          setTimeout(() => {
            onScanDue();
          }, 5000);
        }
        return;
      }
      
      setIsUpdating(false);
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setCountdown(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [scanSchedule, onScanDue]);
  
  return (
    <div 
      className="flex items-center gap-4 px-4 py-2.5 rounded-lg flex-wrap"
      style={{ 
        backgroundColor: 'rgba(34, 197, 94, 0.05)',
        border: '1px solid rgba(34, 197, 94, 0.12)'
      }}
      data-testid="system-status-indicator"
    >
      {/* Pulsing indicator */}
      <div className="flex items-center gap-2">
        <div 
          className="w-2 h-2 rounded-full"
          style={{ 
            backgroundColor: '#22C55E',
            boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)',
            animation: 'pulse-glow 2s ease-in-out infinite'
          }}
        />
        <span className="text-xs font-medium" style={{ color: '#4ade80' }}>
          System Monitoring Active
        </span>
      </div>
      
      {/* Metrics - synced to server timestamps */}
      <div className="flex items-center gap-4 text-xs" style={{ color: '#64748B' }}>
        <span>
          Last evaluation: <span style={{ color: '#94A3B8' }}>{lastEvalTime}</span>
        </span>
        <span>
          Next evaluation in:{' '}
          {isUpdating ? (
            <span style={{ color: '#fbbf24', fontFamily: 'monospace' }}>Updating...</span>
          ) : (
            <span style={{ color: '#94A3B8', fontFamily: 'monospace' }}>{countdown}</span>
          )}
        </span>
      </div>
      
      {/* Market status label */}
      {!isMarketOpen && (
        <span 
          className="text-xs px-2 py-0.5 rounded"
          style={{ backgroundColor: 'rgba(100, 116, 139, 0.2)', color: '#94A3B8' }}
        >
          Post-session analysis active
        </span>
      )}
      
      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};

// Rotating Activity Text Component
const RotatingActivityText = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    const rotateInterval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % SYSTEM_ACTIVITY_MESSAGES.length);
        setIsVisible(true);
      }, 300);
    }, 18000); // Rotate every 18 seconds
    
    return () => clearInterval(rotateInterval);
  }, []);
  
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <Radio 
        className="h-3 w-3" 
        style={{ color: '#3B82F6', animation: 'pulse-subtle 3s ease-in-out infinite' }} 
      />
      <span 
        className="text-xs transition-opacity duration-300"
        style={{ 
          color: '#64748B',
          opacity: isVisible ? 1 : 0
        }}
      >
        {SYSTEM_ACTIVITY_MESSAGES[currentIndex]}
      </span>
      
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Enhanced Featured Stock Card Wrapper
const EnhancedFeaturedStockCard = ({ anomalies, onStockClick }) => {
  // Find the anomaly with the highest unusual_activity_score (not just the first one)
  const topAnomaly = anomalies.length > 0 
    ? anomalies.reduce((max, current) => 
        (current.unusual_activity_score || 0) > (max.unusual_activity_score || 0) ? current : max
      , anomalies[0])
    : null;
  
  if (!topAnomaly) return null;
  
  const score = topAnomaly.unusual_activity_score || 0;
  const changePercent = topAnomaly.daily_return || 0;
  const isPositive = changePercent >= 0;
  
  return (
    <div className="mb-6 relative">
      {/* Scan sweep animation overlay */}
      <div 
        className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.03) 50%, transparent 100%)',
            animation: 'scan-sweep 25s linear infinite'
          }}
        />
      </div>
      
      {/* Card content */}
      <div 
        className="relative p-5 rounded-xl cursor-pointer transition-all duration-300 hover:translate-y-[-2px]"
        style={{ 
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(59, 130, 246, 0.15)',
          zIndex: 2
        }}
        onClick={() => onStockClick(topAnomaly.symbol)}
        data-testid="featured-stock-card-enhanced"
      >
        {/* Top label */}
        <div className="flex items-center justify-between mb-3">
          <span 
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ 
              backgroundColor: 'rgba(59, 130, 246, 0.12)',
              color: '#60A5FA'
            }}
          >
            Top Ranked Anomaly
          </span>
          <span className="text-[10px]" style={{ color: '#64748B' }}>
            Most Interesting Right Now
          </span>
        </div>
        
        {/* Main content */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Symbol */}
            <div>
              <div className="text-2xl font-bold tracking-tight" style={{ color: '#F8FAFC' }}>
                {topAnomaly.symbol}
              </div>
              <div className="text-xs" style={{ color: '#64748B' }}>
                {topAnomaly.company_name || topAnomaly.symbol}
              </div>
            </div>
            
            {/* Price change with pulse glow */}
            <div 
              className="px-4 py-2 rounded-lg relative"
              style={{ 
                backgroundColor: isPositive ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              }}
            >
              {/* Pulse glow effect */}
              <div 
                className="absolute inset-0 rounded-lg"
                style={{
                  boxShadow: isPositive 
                    ? '0 0 20px rgba(34, 197, 94, 0.15)' 
                    : '0 0 20px rgba(239, 68, 68, 0.15)',
                  animation: 'glow-pulse 3s ease-in-out infinite'
                }}
              />
              <span 
                className="text-xl font-bold relative"
                style={{ color: isPositive ? '#4ade80' : '#f87171' }}
              >
                {isPositive ? '+' : ''}{changePercent.toFixed(1)}%
              </span>
            </div>
          </div>
          
          {/* Activity strength */}
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>
              Activity Score
            </div>
            <div 
              className="text-2xl font-bold"
              style={{ 
                color: score >= 70 ? '#f87171' : score >= 40 ? '#fbbf24' : '#4ade80'
              }}
            >
              {Math.round(score)}<span className="text-sm font-normal" style={{ color: '#64748B' }}>/100</span>
            </div>
          </div>
        </div>
        
        {/* Activity level */}
        <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <span className="text-xs" style={{ color: '#64748B' }}>
            {topAnomaly.activity_level || 'Unusual Activity'}
          </span>
          <span 
            className="text-xs flex items-center gap-1"
            style={{ color: '#94A3B8' }}
          >
            Volume: {topAnomaly.volume_multiple?.toFixed(1) || '-'}x normal
          </span>
        </div>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes scan-sweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Micro Metrics Line Component
const MicroMetricsLine = ({ anomalies }) => {
  if (!anomalies || anomalies.length === 0) return null;
  
  const maxScore = Math.max(...anomalies.map(a => a.unusual_activity_score || 0));
  
  return (
    <div 
      className="flex items-center gap-6 px-4 py-2 rounded-lg mb-4"
      style={{ 
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        border: '1px solid rgba(255,255,255,0.04)'
      }}
      data-testid="micro-metrics-line"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: '#64748B' }}>
          Total anomalies flagged this session:
        </span>
        <span className="text-xs font-semibold" style={{ color: '#94A3B8' }}>
          {anomalies.length}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: '#64748B' }}>
          Highest activity score today:
        </span>
        <span 
          className="text-xs font-semibold"
          style={{ 
            color: maxScore >= 70 ? '#f87171' : maxScore >= 40 ? '#fbbf24' : '#4ade80'
          }}
        >
          {Math.round(maxScore)}
        </span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { startTour } = useTour();
  const isMobile = useIsMobile();
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [isConfigured, setIsConfigured] = useState(true);
  const [scanStatus, setScanStatus] = useState(null);
  const [scanSchedule, setScanSchedule] = useState(null); // Server-synced schedule
  const [isMarketOpen, setIsMarketOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeatureText, setUpgradeFeatureText] = useState('this feature');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingStockSymbol, setPendingStockSymbol] = useState(null);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('7d');
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [reviewedStocks, setReviewedStocks] = useState(new Set()); // Track reviewed stocks for progress
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const refreshIntervalRef = useRef(null);
  const dropdownRef = useRef(null);
  const { token, isPremium, isAuthenticated, isEmailVerified, user } = useAuth();

  // Set page title for SEO
  useEffect(() => {
    document.title = 'S&P 500 Stock Anomaly Dashboard | ScopeTheMarket';
  }, []);

  // Handle "How it works" button - start the tutorial
  const handleHowItWorks = () => {
    startTour(TOUR_IDS.DASHBOARD);
  };

  // Handle stock row click - require login to view details
  const handleStockClick = (symbol) => {
    if (!isAuthenticated) {
      setPendingStockSymbol(symbol);
      setShowLoginModal(true);
      return;
    }
    // Track reviewed stocks for progress
    setReviewedStocks(prev => new Set([...prev, symbol]));
    navigate(`/app/stock/${symbol}`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTimeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Silent refresh - uses current selected filter
  const silentRefresh = useCallback(async () => {
    if (isRefreshing || detecting) return;
    
    setIsRefreshing(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const backendTimeRange = getBackendTimeRange(selectedTimeFilter);
      
      console.log(`[Dashboard] Auto-refreshing anomalies (time_range=${backendTimeRange})...`);
      
      const response = await axios.get(`${API_BASE}/anomalies?time_range=${backendTimeRange}`, { headers });
      
      const data = response.data;
      const prevCount = anomalies.length;
      const newCount = (data.anomalies || []).length;
      
      setAnomalies(data.anomalies || []);
      
      // Update server-synced scan schedule
      setScanSchedule({
        last_scan_at: data.last_scan_at,
        next_scan_at: data.next_scan_at,
        scan_interval_seconds: data.scan_interval_seconds
      });
      setIsMarketOpen(data.is_market_hours || false);
      
      setLastUpdated(new Date());
      
      // Show toast if anomalies changed
      if (newCount !== prevCount) {
        toast.success(`Scan complete: ${newCount} anomalies detected`, {
          duration: 3000,
          position: 'bottom-right'
        });
      }
      
      console.log(`[Dashboard] Auto-refresh complete: ${newCount} anomalies`);
    } catch (error) {
      console.error('Silent refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, detecting, token, selectedTimeFilter]);

  // Auto-refresh interval
  useEffect(() => {
    if (anomalies.length > 0) {
      refreshIntervalRef.current = setInterval(silentRefresh, REFRESH_INTERVAL);
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [anomalies.length, silentRefresh]);

  // Re-fetch when auth state changes (login/logout or plan change)
  useEffect(() => {
    fetchAnomalies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPremium]);

  useEffect(() => {
    // Non-blocking: Fetch status after a small delay
    const statusTimeout = setTimeout(() => {
      fetchScanStatus();
    }, 100);
    
    // Refresh scan status every minute
    const statusInterval = setInterval(fetchScanStatus, 60000);
    return () => {
      clearTimeout(statusTimeout);
      clearInterval(statusInterval);
    };
  }, []);

  const fetchScanStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/scan-status`);
      setScanStatus(response.data);
    } catch (error) {
      console.error('Error fetching scan status:', error);
    }
  };

  const fetchAnomalies = async (timeFilter = selectedTimeFilter) => {
    setLoading(true);
    try {
      // Pass auth token for plan-based limits (free: 5, pro: 30)
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Map frontend filter to backend time_range parameter
      const backendTimeRange = getBackendTimeRange(timeFilter);
      
      console.log(`[Dashboard] Fetching anomalies with time_range=${backendTimeRange} (filter: ${timeFilter})`);
      
      // Fetch anomalies - backend handles all filtering
      const response = await axios.get(`${API_BASE}/anomalies?time_range=${backendTimeRange}`, { headers });
      
      // Handle response structure
      const data = response.data;
      const results = data.anomalies || [];
      
      // Update server-synced scan schedule
      setScanSchedule({
        last_scan_at: data.last_scan_at,
        next_scan_at: data.next_scan_at,
        scan_interval_seconds: data.scan_interval_seconds
      });
      setIsMarketOpen(data.is_market_hours || false);
      
      setLastUpdated(new Date());
      
      // Sort by most recent first (by detected_at)
      results.sort((a, b) => {
        const dateA = new Date(a.detected_at || a.date);
        const dateB = new Date(b.detected_at || b.date);
        return dateB - dateA;
      });
      
      console.log(`[Dashboard] Received ${results.length} anomalies (tier: ${data.tier}, from_cache: ${data.from_cache})`);
      
      setAnomalies(results);
    } catch (error) {
      console.error('Error fetching anomalies:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle time filter change
  const handleTimeFilterChange = (filterValue) => {
    // Check if this is a restricted intraday filter for free users
    if (!isPremium && RESTRICTED_INTRADAY.includes(filterValue)) {
      // Don't change the filter, show upgrade modal
      setShowTimeDropdown(false);
      setUpgradeFeatureText('Intraday filters (1H, 4H, 12H, 24H). Upgrade to Pro for faster market research');
      setShowUpgradeModal(true);
      toast.error('Intraday filters are Pro-only');
      return;
    }
    
    setSelectedTimeFilter(filterValue);
    setShowTimeDropdown(false);
    fetchAnomalies(filterValue);
  };

  // Get current filter label
  const getCurrentFilterLabel = () => {
    return TIME_FILTERS.find(f => f.value === selectedTimeFilter)?.label || '24 Hours';
  };

  const loadAnomalies = async () => {
    setDetecting(true);
    try {
      await fetchAnomalies(selectedTimeFilter);
      toast.success(`Loaded anomalies from the last ${getCurrentFilterLabel().toLowerCase()}`);
    } catch (error) {
      toast.error('Failed to load anomalies');
    } finally {
      setDetecting(false);
    }
  };

  const triggerDetection = async () => {
    if (!isAuthenticated) {
      setUpgradeFeatureText('Live Scan');
      setShowUpgradeModal(true);
      return;
    }
    
    if (!isPremium) {
      setUpgradeFeatureText('Live Scan');
      setShowUpgradeModal(true);
      return;
    }
    
    if (!isConfigured) {
      toast.error('Market scanner is not available. Please try again later.');
      return;
    }
    
    if (scanStatus && !scanStatus.is_market_hours) {
      toast.info('Market is currently closed. Showing latest available data.');
      await fetchAnomalies(selectedTimeFilter);
      return;
    }
    
    setDetecting(true);
    try {
      const response = await axios.post(
        `${API_BASE}/detect-anomalies?universe=sp500`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message + ' - This may take 1-2 minutes...');
      
      let attempts = 0;
      const maxAttempts = 12;
      const initialCount = anomalies.length;
      
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const backendTimeRange = getBackendTimeRange(selectedTimeFilter);
          const anomalyResponse = await axios.get(`${API_BASE}/anomalies?limit=50&time_range=${backendTimeRange}`);
          const responseData = anomalyResponse.data;
          const results = responseData.anomalies || [];
          const newCount = results.length;
          
          if (newCount > 0 || newCount !== initialCount || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            
            // No client-side filtering - backend already filtered
            setAnomalies(results);
            setLastUpdated(new Date());
            setDetecting(false);
            
            // Update scan schedule
            if (responseData.last_scan_at) {
              setScanSchedule({
                last_scan_at: responseData.last_scan_at,
                next_scan_at: responseData.next_scan_at,
                scan_interval_seconds: responseData.scan_interval_seconds
              });
            }
            
            if (results.length > 0) {
              toast.success(`Found ${results.length} unusual stocks in the last ${getCurrentFilterLabel().toLowerCase()}`);
            } else {
              toast.info('No unusual activity detected in the selected time range.');
            }
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setDetecting(false);
            toast.error('Scan timed out. Please try again.');
          }
        }
      }, 10000);
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.code === 'SUBSCRIPTION_REQUIRED') {
        setUpgradeFeatureText('this feature');
        setShowUpgradeModal(true);
      } else if (typeof errorDetail === 'string' && errorDetail.includes('API')) {
        toast.error('Market data service unavailable. Please try again later.');
      } else {
        toast.error(errorDetail?.message || errorDetail || 'Detection failed. Please try again.');
      }
      setDetecting(false);
    }
  };

  // Get monitoring interval text
  const getMonitoringIntervalText = () => {
    if (isPremium) {
      return '15 minutes';
    }
    return '4 hours';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      {/* Mobile Header - Only visible on mobile */}
      {isMobile && <MobileHeader />}
      
      {/* Email Verification Banner */}
      {isAuthenticated && !isEmailVerified && (
        <div 
          className="sticky top-0 z-[60] px-4 py-2 md:py-3 flex items-center justify-center gap-2 md:gap-3 flex-wrap"
          style={{ backgroundColor: '#F59E0B', color: '#1F2937' }}
        >
          <svg className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="font-medium text-xs md:text-sm text-center">
            Please verify your email to access all features.
          </span>
          <button
            onClick={() => navigate('/check-email', { state: { email: user?.email } })}
            className="px-2 md:px-3 py-1 text-xs md:text-sm font-medium rounded-md flex-shrink-0"
            style={{ backgroundColor: '#1F2937', color: 'white' }}
          >
            Verify Now
          </button>
        </div>
      )}
      
      {/* Desktop Header - Hidden on mobile */}
      <div className="hidden md:block sticky top-0 z-50 backdrop-blur-md" style={{ backgroundColor: 'rgba(11,15,26,0.95)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-4 flex-wrap">
                <h1 className="text-xl font-semibold tracking-tight" style={{ color: '#F8FAFC' }}>
                  Market Overview
                </h1>
                <MarketStatusBadge />
              </div>
              
              {/* Description and rotating activity */}
              <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                Stocks that moved more than they usually do
              </p>
              <RotatingActivityText />
            </div>
            
            <div className="flex items-center gap-2">
              {/* Refresh */}
              <Button
                onClick={silentRefresh}
                variant="ghost"
                size="sm"
                disabled={isRefreshing}
                className="hover:bg-white/5"
                style={{ color: '#94A3B8' }}
                data-testid="refresh-button"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              {/* How it works */}
              <Button
                onClick={handleHowItWorks}
                variant="ghost"
                size="sm"
                className="hover:bg-white/5 text-sm"
                style={{ color: '#94A3B8' }}
                data-testid="help-button"
              >
                <HelpCircle className="h-4 w-4 mr-1.5" />
                How it works
              </Button>
              
              {/* Time Filter Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <Button
                  onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                  size="sm"
                  variant="outline"
                  className="text-sm font-medium border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="time-filter-button"
                >
                  <Clock className="h-4 w-4 mr-1.5" />
                  {getCurrentFilterLabel()}
                  <ChevronDown className="h-4 w-4 ml-1.5" />
                </Button>
                
                {showTimeDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-44 rounded-md shadow-lg z-50"
                    style={{ backgroundColor: '#1E293B', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {TIME_FILTERS.map((filter) => {
                      const isRestricted = filter.proOnly && !isPremium;
                      return (
                        <button
                          key={filter.value}
                          onClick={() => handleTimeFilterChange(filter.value)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-700 first:rounded-t-md last:rounded-b-md flex items-center justify-between ${
                            selectedTimeFilter === filter.value ? 'text-blue-400 bg-slate-700/50' : 'text-slate-300'
                          }`}
                        >
                          <span>{filter.label}</span>
                          {isRestricted && (
                            <span 
                              className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                              style={{ 
                                backgroundColor: 'rgba(139, 92, 246, 0.2)', 
                                color: '#A78BFA'
                              }}
                            >
                              <Lock className="h-3 w-3" />
                              Pro
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Load Anomalies Button */}
              <Button
                onClick={loadAnomalies}
                disabled={detecting}
                size="sm"
                className="text-sm font-medium bg-blue-600 text-white hover:bg-blue-500"
                data-testid="load-anomalies-button"
              >
                {detecting ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Activity className="mr-1.5 h-4 w-4" />
                    Load Anomalies
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* System Status Indicator - uses server-synced timestamps */}
          <div className="mt-4">
            <SystemStatusIndicator 
              scanSchedule={scanSchedule}
              isMarketOpen={isMarketOpen}
              onScanDue={silentRefresh}
            />
          </div>
          
          {/* Monitoring info line */}
          <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: '#64748B' }}>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3 w-3" style={{ color: isPremium ? '#4ade80' : '#94A3B8' }} />
              Monitoring interval: <span style={{ color: isPremium ? '#4ade80' : '#94A3B8' }}>{getMonitoringIntervalText()}</span>
            </span>
            {!isMarketOpen && (
              <span className="flex items-center gap-1.5">
                <span style={{ color: '#94A3B8' }}>•</span>
                Market Status: <span style={{ color: '#fbbf24' }}>Closed</span>
                <span style={{ color: '#64748B' }}>- Post-session analysis active</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        feature={upgradeFeatureText}
      />

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md" style={{ backgroundColor: '#0E1324', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#F8FAFC' }}>
              <Lock className="h-5 w-5" style={{ color: '#3B82F6' }} />
              Sign In Required
            </DialogTitle>
            <DialogDescription style={{ color: '#94A3B8' }}>
              Create a free account or sign in to view detailed stock analysis for {pendingStockSymbol}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={() => {
                setShowLoginModal(false);
                navigate('/login', { state: { returnTo: `/app/stock/${pendingStockSymbol}` } });
              }}
              className="w-full bg-blue-600 text-white hover:bg-blue-500"
              data-testid="login-modal-signin-button"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign In
            </Button>
            <Button
              onClick={() => {
                setShowLoginModal(false);
                navigate('/signup', { state: { returnTo: `/app/stock/${pendingStockSymbol}` } });
              }}
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="login-modal-signup-button"
            >
              Create Free Account
            </Button>
            <Button
              onClick={() => setShowLoginModal(false)}
              variant="ghost"
              className="w-full text-slate-400 hover:text-slate-300"
              data-testid="login-modal-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help Panel - Desktop only */}
      {showHelp && !isMobile && (
        <div className="max-w-[1400px] mx-auto px-6 pt-4">
          <div className="p-5 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
            <h3 className="text-sm font-medium mb-3" style={{ color: '#E2E8F0' }}>How It Works</h3>
            <div className="grid md:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="font-medium mb-1" style={{ color: '#94A3B8' }}>What this shows</p>
                <p style={{ color: '#64748B' }}>Stocks with unusual price moves or trading volume compared to their own history.</p>
              </div>
              <div>
                <p className="font-medium mb-1" style={{ color: '#94A3B8' }}>How we measure</p>
                <p style={{ color: '#64748B' }}>Each stock is compared to itself. A 3% move might be rare for a stable stock but normal for a volatile one.</p>
              </div>
              <div>
                <p className="font-medium mb-1" style={{ color: '#94A3B8' }}>What to do</p>
                <p style={{ color: '#64748B' }}>Click any row to see detailed analysis. This is for research only - not investment advice.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MOBILE LAYOUT ==================== */}
      {isMobile && (
        <div className="px-4 py-4 pb-24" data-testid="mobile-dashboard">
          {/* Mobile: Market Pulse */}
          {!loading && anomalies.length > 0 && (
            <MarketPulse 
              anomalies={anomalies}
              lastScanAt={scanSchedule?.last_scan_at}
              isMarketOpen={isMarketOpen}
            />
          )}
          
          {/* Mobile: Progress Tracker */}
          {!loading && anomalies.length > 0 && isAuthenticated && (
            <ProgressTracker 
              reviewed={reviewedStocks.size}
              total={anomalies.length}
            />
          )}
          
          {/* Mobile: Filter Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: '#94A3B8' }}>
                {anomalies.length} anomalies
              </span>
              <span 
                className="text-xs px-2 py-0.5 rounded"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#60A5FA' }}
              >
                {TIME_FILTERS.find(f => f.value === selectedTimeFilter)?.label || '7 Days'}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Refresh button */}
              <button
                onClick={silentRefresh}
                disabled={isRefreshing}
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                data-testid="mobile-refresh-button"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} style={{ color: '#94A3B8' }} />
              </button>
              
              {/* Filter button */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ 
                  backgroundColor: showMobileFilters ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255,255,255,0.05)',
                  border: showMobileFilters ? '1px solid rgba(59, 130, 246, 0.3)' : 'none'
                }}
                data-testid="mobile-filter-button"
              >
                <Filter className="h-4 w-4" style={{ color: showMobileFilters ? '#60A5FA' : '#94A3B8' }} />
              </button>
            </div>
          </div>
          
          {/* Mobile: Filter dropdown */}
          {showMobileFilters && (
            <div 
              className="mb-4 p-3 rounded-xl"
              style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p className="text-xs font-medium mb-2" style={{ color: '#64748B' }}>Time Range</p>
              <div className="flex flex-wrap gap-2">
                {TIME_FILTERS.map((filter) => {
                  const isRestricted = filter.proOnly && !isPremium;
                  const isSelected = selectedTimeFilter === filter.value;
                  
                  return (
                    <button
                      key={filter.value}
                      onClick={() => {
                        if (isRestricted) {
                          setUpgradeFeatureText('Intraday filters');
                          setShowUpgradeModal(true);
                        } else {
                          handleTimeFilterChange(filter.value);
                          setShowMobileFilters(false);
                        }
                      }}
                      className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                      style={{ 
                        backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                        color: isSelected ? '#60A5FA' : '#94A3B8',
                        border: isSelected ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
                        minHeight: '44px'
                      }}
                    >
                      {filter.label}
                      {isRestricted && <Lock className="h-3 w-3" style={{ color: '#A78BFA' }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Mobile: Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin mb-3" style={{ color: '#3B82F6' }} />
              <p className="text-sm" style={{ color: '#94A3B8' }}>Loading market data...</p>
            </div>
          )}
          
          {/* Mobile: Empty State */}
          {!loading && anomalies.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <Activity className="h-12 w-12 mb-4" style={{ color: '#334155' }} />
              <p className="text-base font-medium mb-2" style={{ color: '#94A3B8' }}>No unusual activity detected</p>
              <p className="text-sm mb-6" style={{ color: '#64748B' }}>
                Tap below to check for recent unusual stock movements
              </p>
              <Button
                onClick={loadAnomalies}
                className="bg-blue-600 text-white hover:bg-blue-500 w-full max-w-xs"
                style={{ minHeight: '48px' }}
                data-testid="mobile-empty-state-load-button"
              >
                <Activity className="mr-2 h-4 w-4" />
                Load Anomalies
              </Button>
            </div>
          )}
          
          {/* Mobile: Anomaly Cards List */}
          {!loading && anomalies.length > 0 && (
            <div className="space-y-3" data-testid="mobile-anomaly-list">
              {anomalies.map((anomaly, index) => (
                <MobileAnomalyCard
                  key={anomaly.symbol}
                  anomaly={anomaly}
                  onClick={handleStockClick}
                  isNew={index < 3} // First 3 are "new"
                  index={index}
                />
              ))}
            </div>
          )}
          
          {/* Mobile: Bottom hint */}
          {!loading && anomalies.length > 0 && (
            <div className="mt-6 text-center">
              <p className="text-xs" style={{ color: '#52525b' }}>
                Tap any stock to see detailed analysis
              </p>
            </div>
          )}
        </div>
      )}

      {/* ==================== DESKTOP LAYOUT ==================== */}
      {!isMobile && (
        <div className="max-w-[1400px] mx-auto px-6 py-6" data-testid="desktop-dashboard">
          
          {/* Micro Metrics Line */}
          {!loading && anomalies.length > 0 && (
            <MicroMetricsLine anomalies={anomalies} />
          )}
          
          {/* Enhanced Featured Stock Card - Most Interesting Right Now */}
          {!loading && anomalies.length > 0 && (
            <EnhancedFeaturedStockCard 
              anomalies={anomalies} 
              onStockClick={handleStockClick}
            />
          )}

          {/* Tip Banner */}
          {anomalies.length > 0 && (
            <div 
              className="mb-6 px-4 py-3 rounded-lg flex items-center gap-2"
              style={{ backgroundColor: 'rgba(74, 222, 128, 0.08)', border: '1px solid rgba(74, 222, 128, 0.15)' }}
              data-testid="tip-banner"
            >
              <Lightbulb className="h-4 w-4 flex-shrink-0" style={{ color: '#4ade80' }} />
              <p className="text-sm" style={{ color: '#CBD5E1' }}>
                <span className="font-medium" style={{ color: '#4ade80' }}>Tip:</span>{' '}
                Start with the top 5 stocks - they show the most <strong style={{ color: '#F8FAFC' }}>unusual activity today</strong>.
              </p>
            </div>
          )}

          {/* Time Range Label */}
          {anomalies.length > 0 && (
            <div className="mb-4 flex items-center gap-2" data-testid="time-range-label">
              <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#94A3B8' }}>
                Filter: {TIME_FILTERS.find(f => f.value === selectedTimeFilter)?.label || '7 Days'} • {anomalies.length} results
              </span>
            </div>
          )}

          {/* Table Container */}
          <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#0E1324', border: '1px solid rgba(255,255,255,0.06)' }}>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-10 w-10 animate-spin mb-4" style={{ color: '#3B82F6' }} />
                <p className="text-sm" style={{ color: '#94A3B8' }}>Loading market data...</p>
              </div>
            ) : anomalies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Activity className="h-12 w-12 mb-4" style={{ color: '#334155' }} />
                <p className="text-lg font-medium mb-2" style={{ color: '#94A3B8' }}>No unusual activity detected</p>
                <p className="text-sm mb-6" style={{ color: '#64748B' }}>
                  Click "Load Anomalies" to check for recent unusual stock movements
                </p>
                <Button
                  onClick={loadAnomalies}
                  className="bg-blue-600 text-white hover:bg-blue-500"
                  data-testid="empty-state-load-button"
                >
                  <Activity className="mr-2 h-4 w-4" />
                  Load Anomalies
                </Button>
              </div>
            ) : (
              <AdvancedTable 
                data={anomalies} 
                onRowClick={handleStockClick}
              />
            )}
          </div>
          
          {/* Financial Disclaimer */}
          <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(148, 163, 184, 0.05)', border: '1px solid rgba(148, 163, 184, 0.1)' }}>
            <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>
              <strong style={{ color: '#94A3B8' }}>Disclaimer:</strong> ScopeTheMarket provides market research and intelligence only. 
              The information presented is for educational and informational purposes and should not be construed as investment advice, 
              trading recommendations, or a solicitation to buy or sell securities. Past performance does not guarantee future results. 
              Users are solely responsible for their own investment decisions. We are not liable for any financial losses.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
